import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { 
  BookOpen, Heart, GraduationCap, LogOut, Sparkles, RefreshCw, 
  FileText, Copy, Printer, Check, AlertCircle, Loader2, 
  Save, FolderOpen, Trash2, X, Share2, Settings,
  Search, LayoutTemplate, Shield, Globe, FileDown, Edit3,
  Mic, Plus, Calendar, Mail, CreditCard, Presentation, Menu, HardDrive, Image,
  ChevronDown, BookMarked, HelpCircle, Layers, ArrowLeft, ChevronRight, Users,
  Lightbulb, ClipboardList, Activity, MessageSquare, Monitor, Volume2
} from 'lucide-react';
import AdminPanel from '../components/AdminPanel';
import ScriptureSearch from '../components/ScriptureSearch';
import DocumentStorage from '../components/DocumentStorage';
import Scheduler from '../components/Scheduler';
import TemplateLibrary from '../components/TemplateLibrary';
import SettingsModal from '../components/SettingsModal';
import SermonPlanner from '../components/SermonPlanner';
import EmailShareModal from '../components/EmailShareModal';
import ReligionCompare from '../components/ReligionCompare';
import TodoTasks from '../components/TodoTasks';
import TeamManagement from '../components/TeamManagement';
import SermonIllustrations from '../components/SermonIllustrations';
import LessonHandout from '../components/LessonHandout';
import CounselingNotes from '../components/CounselingNotes';
import FlyerGenerator from '../components/FlyerGenerator';
import SupportCenter from '../components/SupportCenter';
import QuizManager from '../components/QuizManager';
import QuizEditor from '../components/QuizEditor';
import QuizPresenterLive from '../components/QuizPresenterLive';
import PresentationManager from '../components/PresentationManager';
import SlideEditor from '../components/SlideEditor';
import PresentMode from '../components/PresentMode';
import SermonNotes from '../components/SermonNotes';
import ProPresenter from '../components/ProPresenter';
import ScriptureLookupBar from '../components/ScriptureLookupBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

const AUDIENCES = [
  'General Congregation', 'Youth / Teens', "Men's Ministry", "Women's Ministry",
  'Young Adults', 'Children', 'Small Group', 'Leadership'
];

const TONES = ['Inspirational', 'Academic / Deep Study', 'Conversational', 'Evangelistic'];
const FOCUS_AREAS = ['Prayer', 'Worship', 'Evangelism', 'Discipleship', 'Healing', 'Faith'];

const DEVOTIONAL_DURATIONS = [
  { value: 'daily', label: 'Daily (7 days)' },
  { value: 'weekly', label: 'Weekly (4 weeks)' },
  { value: 'monthly', label: 'Monthly (30 days)' }
];

const ENTRY_LENGTHS = [
  { value: 'brief', label: 'Brief (150-200 words)' },
  { value: 'standard', label: 'Standard (300-400 words)' },
  { value: 'extended', label: 'Extended (500+ words)' }
];

const TEACHING_FORMATS = [
  'Sunday Message', 'Wednesday Bible Study', 'Workshop',
  'Conference Session', 'Youth Lesson', 'Small Group'
];

const SESSION_LENGTHS = ['30 min', '45 min', '60 min', '90 min'];

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'pt', label: 'Português' },
  { code: 'de', label: 'Deutsch' },
  { code: 'ko', label: '한국어' },
  { code: 'zh', label: '中文' }
];

const DENOMINATIONS = [
  'Non-denominational',
  'Baptist',
  'Pentecostal',
  'Methodist',
  'Presbyterian',
  'Lutheran',
  'Catholic',
  'Anglican / Episcopal',
  'Church of God',
  'Assemblies of God',
  'Seventh-day Adventist',
  'Church of Christ',
  'Reformed',
  'Charismatic',
  'Evangelical',
  'Other'
];

export default function MainApp() {
  const { user, logout, t, language } = useAuth();
  const navigate = useNavigate();
  const [mode, setModeRaw] = useState(() => {
    return sessionStorage.getItem('dsashun_mode') || 'dashboard';
  });
  const setMode = (newMode) => {
    setModeRaw(newMode);
    sessionStorage.setItem('dsashun_mode', newMode);
  };
  const [content, setContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingProgress, setGeneratingProgress] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [savedItems, setSavedItems] = useState([]);
  const [saveTitle, setSaveTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [currentContentId, setCurrentContentId] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingPPTX, setIsExportingPPTX] = useState(false);
  const [customDenomination, setCustomDenomination] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [coverImageLoading, setCoverImageLoading] = useState(false);
  
  const previewRef = useRef(null);
  
  // Modal states
  const [showAdmin, setShowAdmin] = useState(false);
  const [showScripture, setShowScripture] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showPlanner, setShowPlanner] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showStorage, setShowStorage] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [showTodo, setShowTodo] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const [showIllustrations, setShowIllustrations] = useState(false);
  const [showHandout, setShowHandout] = useState(false);
  const [showCounseling, setShowCounseling] = useState(false);
  const [showFlyer, setShowFlyer] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizEditId, setQuizEditId] = useState(null);
  const [quizPresentId, setQuizPresentId] = useState(null);
  const [showPresentation, setShowPresentation] = useState(false);
  const [presEditId, setPresEditId] = useState(null);
  const [presPresentId, setPresPresentId] = useState(null);
  const [showSermonNotes, setShowSermonNotes] = useState(false);
  const [showProPresenter, setShowProPresenter] = useState(false);
  const [showScriptureLookup, setShowScriptureLookup] = useState(false);
  const [activityFeed, setActivityFeed] = useState([]);
  
  // Trial & Search states
  const [trialDismissed, setTrialDismissed] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  
  // Dashboard stats
  const [dashStats, setDashStats] = useState(null);
  const [showEmailShare, setShowEmailShare] = useState(false);
  const [quickCreating, setQuickCreating] = useState(false);

  // Guided tour
  const [tourStep, setTourStep] = useState(-1);
  const tourKey = user?.id ? `dsashun_tour_${user.id}` : null;

  // Global keyboard shortcut: Ctrl+K / Cmd+K for scripture lookup
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowScriptureLookup(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (tourKey && !localStorage.getItem(tourKey)) {
      setTourStep(0);
    }
  }, [tourKey]);

  const TOUR_STEPS = [
    { title: 'Welcome to dsashun!', text: 'Your AI-powered ministry content hub. Let us show you around.', target: null },
    { title: 'Quick Create', text: 'Generate a complete 1-week Bible study instantly with a single click.', target: 'quick-create-card' },
    { title: 'AI Generators', text: 'Create Bible studies, devotionals, curriculum, and sermon notes — all AI-powered.', target: 'generators-section' },
    { title: 'Ministry Tools', text: 'Schedule services, plan sermon series, compare religions, and look up scripture.', target: 'tools-section' },
    { title: 'Your Content', text: 'Access your saved library, documents, templates, and search past creations.', target: 'content-section' },
    { title: 'You\'re all set!', text: 'Start by clicking any card below, or try Quick Create for an instant Bible study.', target: null },
  ];

  const advanceTour = () => {
    if (tourStep >= TOUR_STEPS.length - 1) {
      setTourStep(-1);
      if (tourKey) localStorage.setItem(tourKey, '1');
    } else {
      setTourStep(tourStep + 1);
    }
  };

  const skipTour = () => {
    setTourStep(-1);
    if (tourKey) localStorage.setItem(tourKey, '1');
  };

  // Form states
  const [bibleStudyForm, setBibleStudyForm] = useState({
    series_title: '', scriptures: '', num_weeks: 4, audience: 'General Congregation',
    tone: 'Inspirational', theme_month: '', focus_areas: [], denomination: '', language: 'en'
  });

  const [devotionalForm, setDevotionalForm] = useState({
    series_title: '', scriptures: '', audience: 'General Congregation', tone: 'Inspirational',
    duration: 'daily', entry_length: 'standard', include_prayer: true,
    include_declaration: true, denomination: '', language: 'en'
  });

  const [curriculumForm, setCurriculumForm] = useState({
    series_title: '', scriptures: '', num_sessions: 4, audience: 'General Congregation',
    tone: 'Inspirational', teaching_format: 'Sunday Message', session_length: '45 min',
    include_altar_call: true, include_small_group: true, denomination: '', language: 'en'
  });

  const [sermonNotesForm, setSermonNotesForm] = useState({
    sermon_title: '', scriptures: '', main_points: ['', '', ''], key_verses: [],
    audience: 'General Congregation', language: 'en'
  });

  // Custom template form
  const [templateForm, setTemplateForm] = useState({
    name: '', category: 'custom', description: '', default_scriptures: '',
    default_weeks: 4, focus_areas: [], outline: ''
  });

  const getCurrentForm = () => {
    switch (mode) {
      case 'bible-study': return bibleStudyForm;
      case 'devotional': return devotionalForm;
      case 'curriculum': return curriculumForm;
      case 'sermon-notes': return sermonNotesForm;
      default: return bibleStudyForm;
    }
  };

  const setCurrentForm = (data) => {
    switch (mode) {
      case 'bible-study': setBibleStudyForm(prev => ({ ...prev, ...data })); break;
      case 'devotional': setDevotionalForm(prev => ({ ...prev, ...data })); break;
      case 'curriculum': setCurriculumForm(prev => ({ ...prev, ...data })); break;
      case 'sermon-notes': setSermonNotesForm(prev => ({ ...prev, ...data })); break;
    }
  };

  const handleTemplateSelect = (template) => {
    if (template.content_type === 'sermon-notes') {
      setMode('sermon-notes');
      setSermonNotesForm(prev => ({
        ...prev,
        sermon_title: template.name,
        scriptures: template.default_scriptures
      }));
    } else {
      setMode(template.content_type);
      const formData = {
        series_title: template.name,
        scriptures: template.default_scriptures,
        focus_areas: template.focus_areas || []
      };
      if (template.content_type === 'bible-study') {
        setBibleStudyForm(prev => ({ ...prev, ...formData, num_weeks: template.default_weeks }));
      } else if (template.content_type === 'devotional') {
        setDevotionalForm(prev => ({ ...prev, ...formData }));
      } else {
        setCurriculumForm(prev => ({ ...prev, ...formData, num_sessions: template.default_weeks }));
      }
    }
  };

  // AI Cover Image Generation
  const triggerCoverImage = async (title, topic) => {
    setCoverImageLoading(true);
    try {
      const res = await axios.post(`${API}/generate/cover-image`, { title, topic });
      if (res.data.job_id) {
        const imgJobId = res.data.job_id;
        for (let i = 0; i < 30; i++) {
          await new Promise(r => setTimeout(r, 3000));
          const imgStatus = await axios.get(`${API}/generate/cover-image/${imgJobId}`);
          if (imgStatus.data.status === 'completed' && imgStatus.data.image_base64) {
            setCoverImage(`data:image/png;base64,${imgStatus.data.image_base64}`);
            break;
          } else if (imgStatus.data.status === 'failed') {
            break;
          }
        }
      }
    } catch (e) {
      // Cover image is optional — fail silently
    } finally {
      setCoverImageLoading(false);
    }
  };

  const handleGenerate = async () => {
    setError('');
    setIsGenerating(true);
    setCurrentContentId(null);
    setIsEditing(false);
    setCoverImage(null);
    
    const formData = getCurrentForm();
    
    // Validation
    if (mode === 'sermon-notes') {
      if (!formData.sermon_title.trim()) {
        setError('Please enter a sermon title');
        setIsGenerating(false);
        return;
      }
      if (!formData.scriptures.trim()) {
        setError('Please enter scripture references');
        setIsGenerating(false);
        return;
      }
      const validPoints = formData.main_points.filter(p => p.trim());
      if (validPoints.length === 0) {
        setError('Please enter at least one main point');
        setIsGenerating(false);
        return;
      }
    } else {
      if (!formData.series_title?.trim()) {
        setError('Please enter a series title');
        setIsGenerating(false);
        return;
      }
      if (!formData.scriptures?.trim()) {
        setError('Please enter at least one scripture reference');
        setIsGenerating(false);
        return;
      }
    }

    try {
      const endpoint = mode === 'sermon-notes' ? 'sermon-notes' : mode;
      const payload = mode === 'sermon-notes' 
        ? { ...formData, main_points: formData.main_points.filter(p => p.trim()) }
        : formData;
      
      const response = await axios.post(`${API}/generate/${endpoint}`, payload, { timeout: 30000 });
      
      if (response.data.job_id) {
        // Poll for result
        const jobId = response.data.job_id;
        let attempts = 0;
        const maxAttempts = 60; // 5 minutes max
        
        while (attempts < maxAttempts) {
          await new Promise(r => setTimeout(r, 3000));
          attempts++;
          
          try {
            const status = await axios.get(`${API}/generate/status/${jobId}`, { timeout: 10000 });
            
            if (status.data.progress) {
              setGeneratingProgress(status.data.progress);
            }
            
            if (status.data.status === 'completed') {
              setContent(status.data.content || '');
              setEditedContent(status.data.content || '');
              setGeneratingProgress('');
              triggerCoverImage(formData.series_title || formData.sermon_title || 'Bible Study', formData.scriptures || '');
              break;
            } else if (status.data.status === 'failed') {
              setError(status.data.error || 'Generation failed. Please try again.');
              setGeneratingProgress('');
              break;
            }
          } catch (pollErr) {
            // Continue polling on network errors
            if (attempts >= maxAttempts) {
              setError('Generation timed out. Please try again.');
              setGeneratingProgress('');
            }
          }
        }
      } else {
        setContent(response.data.content);
        setEditedContent(response.data.content);
        triggerCoverImage(formData.series_title || formData.sermon_title || 'Bible Study', formData.scriptures || '');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Generation failed. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleQuickCreate = async () => {
    setError('');
    setQuickCreating(true);
    setIsGenerating(true);
    setCurrentContentId(null);
    setIsEditing(false);
    setMode('bible-study');

    try {
      const response = await axios.post(`${API}/generate/quick-create`, {}, { timeout: 30000 });
      if (response.data.job_id) {
        const jobId = response.data.job_id;
        let attempts = 0;
        const maxAttempts = 60;
        while (attempts < maxAttempts) {
          await new Promise(r => setTimeout(r, 3000));
          attempts++;
          try {
            const status = await axios.get(`${API}/generate/status/${jobId}`, { timeout: 10000 });
            if (status.data.progress) setGeneratingProgress(status.data.progress);
            if (status.data.status === 'completed') {
              setContent(status.data.content || '');
              setEditedContent(status.data.content || '');
              setGeneratingProgress('');
              triggerCoverImage(response.data.topic || 'Bible Study', '');
              break;
            } else if (status.data.status === 'failed') {
              setError(status.data.error || 'Generation failed. Please try again.');
              setGeneratingProgress('');
              break;
            }
          } catch (pollErr) {
            if (attempts >= maxAttempts) {
              setError('Generation timed out. Please try again.');
              setGeneratingProgress('');
            }
          }
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Quick create failed. Please try again.');
    } finally {
      setQuickCreating(false);
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(isEditing ? editedContent : content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const logoHtml = user?.logo_url ? `<img src="${user.logo_url}" style="height: 60px; margin-bottom: 20px;" />` : '';
    const churchHtml = user?.church_name ? `<p style="color: #B8860B; text-transform: uppercase; letter-spacing: 0.1em; font-size: 12px; margin-bottom: 20px;">${user.church_name}</p>` : '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${getCurrentForm().series_title || getCurrentForm().sermon_title || 'Bible Study'}</title>
          <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Merriweather:wght@300;400;700&display=swap" rel="stylesheet">
          <style>
            body { font-family: 'Merriweather', Georgia, serif; line-height: 1.8; padding: 40px; max-width: 800px; margin: 0 auto; color: #0F1A2A; }
            .header { text-align: center; margin-bottom: 40px; }
            h1 { font-family: 'Cormorant Garamond', serif; color: #1B3A6B; border-bottom: 2px solid #B8860B; padding-bottom: 8px; }
            h2 { font-family: 'Cormorant Garamond', serif; color: white; background: #1B3A6B; padding: 12px 16px; border-radius: 4px; }
            h3 { color: #B8860B; text-transform: uppercase; font-size: 14px; letter-spacing: 0.05em; border-bottom: 1px solid #E2DFD8; padding-bottom: 4px; }
            blockquote { background: #FFF8E7; border-left: 4px solid #B8860B; padding: 16px 24px; margin: 24px 0; font-style: italic; }
            hr { border: none; border-top: 2px dashed #E2DFD8; margin: 32px 0; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">${logoHtml}${churchHtml}</div>
          ${previewRef.current?.innerHTML || ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleExportPDF = async () => {
    if (!previewRef.current) return;
    setIsExportingPDF(true);
    
    try {
      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      
      // Add logo if exists
      if (user?.logo_url && user.logo_url.startsWith('data:')) {
        pdf.addImage(user.logo_url, 'PNG', pdfWidth / 2 - 15, 10, 30, 20);
      }
      
      // Add church name
      if (user?.church_name) {
        pdf.setFontSize(10);
        pdf.setTextColor(184, 134, 11);
        pdf.text(user.church_name, pdfWidth / 2, 35, { align: 'center' });
      }
      
      // Calculate pages
      const pageHeight = pdfHeight - 50;
      const scaledHeight = imgHeight * ratio;
      let heightLeft = scaledHeight;
      let position = 45;
      
      pdf.addImage(imgData, 'PNG', imgX, position, imgWidth * ratio, imgHeight * ratio);
      heightLeft -= pageHeight;
      
      while (heightLeft > 0) {
        position = heightLeft - scaledHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', imgX, position, imgWidth * ratio, imgHeight * ratio);
        heightLeft -= pageHeight;
      }
      
      const title = getCurrentForm().series_title || getCurrentForm().sermon_title || 'bible-study';
      pdf.save(`${title.replace(/\s+/g, '-').toLowerCase()}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
      setError('Failed to export PDF');
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleExportWord = async () => {
    const { Document, Packer, Paragraph, TextRun, HeadingLevel, BorderStyle, AlignmentType, ShadingType } = await import('docx');
    
    const contentToExport = isEditing ? editedContent : content;
    const title = getCurrentForm().series_title || getCurrentForm().sermon_title || 'Bible Study';
    const lines = contentToExport.split('\n');
    const children = [];

    // Title header with branding
    children.push(new Paragraph({
      children: [new TextRun({ text: user?.church_name?.toUpperCase() || '', size: 20, color: 'B8860B', bold: true, font: 'Georgia' })],
      spacing: { after: 100 },
      alignment: AlignmentType.CENTER,
    }));
    children.push(new Paragraph({
      children: [new TextRun({ text: title, size: 48, color: '1B3A6B', bold: true, font: 'Georgia' })],
      spacing: { after: 80 },
      alignment: AlignmentType.CENTER,
    }));
    children.push(new Paragraph({
      children: [new TextRun({ text: 'Generated with dsashun', size: 18, color: '94A3B8', italics: true, font: 'Georgia' })],
      spacing: { after: 200 },
      alignment: AlignmentType.CENTER,
    }));
    children.push(new Paragraph({ text: '', spacing: { after: 300 }, border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: 'B8860B' } } }));
    
    for (const line of lines) {
      if (line.startsWith('# ')) {
        children.push(new Paragraph({
          children: [new TextRun({ text: line.replace('# ', ''), size: 36, color: '1B3A6B', bold: true, font: 'Georgia' })],
          spacing: { before: 400, after: 200 },
          shading: { type: ShadingType.SOLID, color: 'F4F2EC' },
        }));
      } else if (line.startsWith('## ')) {
        children.push(new Paragraph({
          children: [new TextRun({ text: line.replace('## ', ''), size: 28, color: '1B3A6B', bold: true, font: 'Georgia' })],
          spacing: { before: 400, after: 100 },
        }));
        children.push(new Paragraph({ text: '', spacing: { after: 100 }, border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: 'B8860B' } } }));
      } else if (line.startsWith('### ')) {
        children.push(new Paragraph({
          children: [new TextRun({ text: line.replace('### ', '').toUpperCase(), size: 20, color: 'B8860B', bold: true, font: 'Georgia' })],
          spacing: { before: 300, after: 100 },
        }));
      } else if (line.startsWith('> ')) {
        children.push(new Paragraph({ children: [new TextRun({ text: line.replace('> ', ''), italics: true, color: '1B3A6B', font: 'Georgia' })], indent: { left: 720 }, spacing: { before: 200, after: 200 }, border: { left: { style: BorderStyle.SINGLE, size: 24, color: 'B8860B' } } }));
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        children.push(new Paragraph({ text: line.replace(/^[-*]\s/, ''), bullet: { level: 0 }, spacing: { after: 100 } }));
      } else if (line.match(/^\d+\. /)) {
        children.push(new Paragraph({ text: line, spacing: { after: 100 } }));
      } else if (line.startsWith('---')) {
        children.push(new Paragraph({ text: '', spacing: { before: 300, after: 300 }, border: { bottom: { style: BorderStyle.DASHED, size: 6, color: 'E2DFD8' } } }));
      } else if (line.includes('_____')) {
        children.push(new Paragraph({ text: line, spacing: { after: 150 } }));
      } else if (line.trim()) {
        children.push(new Paragraph({ text: line, spacing: { after: 150 } }));
      }
    }

    const doc = new Document({ sections: [{ properties: {}, children }] });
    const blob = await Packer.toBlob(doc);
    const { saveAs } = await import('file-saver');
    saveAs(blob, `${title}.docx`);
  };

  const handleExportPPTX = async () => {
    setIsExportingPPTX(true);
    try {
      const contentToExport = isEditing ? editedContent : content;
      const title = getCurrentForm().series_title || getCurrentForm().sermon_title || 'Bible Study';
      const response = await axios.post(`${API}/export/pptx`, {
        content: contentToExport,
        title,
        church_name: user?.church_name || '',
        cover_image_base64: coverImage ? coverImage.replace(/^data:image\/\w+;base64,/, '') : null,
      }, { responseType: 'blob' });
      
      const { saveAs } = await import('file-saver');
      saveAs(response.data, `${title.replace(/\s+/g, '-').toLowerCase()}.pptx`);
    } catch (err) {
      console.error('PPTX export error:', err);
      setError('Failed to export PowerPoint');
    } finally {
      setIsExportingPPTX(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    try {
      const res = await axios.get(`${API}/search`, { params: { q: searchQuery } });
      setSearchResults(res.data.results);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const loadSearchResult = async (item) => {
    if (item.content_type === 'sermon-plan') {
      setShowSearch(false);
      setShowPlanner(true);
      return;
    }
    try {
      const res = await axios.get(`${API}/content/${item.id}`);
      setContent(res.data.content);
      setCurrentContentId(item.id);
      setShowSearch(false);
    } catch (err) {
      console.error('Load error:', err);
    }
  };

  const exitImpersonation = async () => {
    try {
      const res = await axios.post(`${API}/admin/exit-impersonation`, {});
      if (res.data.access_token) {
        localStorage.setItem('access_token', res.data.access_token);
      }
      window.location.href = '/app';
    } catch (err) {
      console.error('Exit impersonation error:', err);
      // Fallback: clear token and reload
      localStorage.removeItem('access_token');
      window.location.href = '/app';
    }
  };

  // Trial info
  const isTrialActive = user?.trial_active;
  const trialEndDate = user?.trial_end_date;
  const trialDaysLeft = trialEndDate ? Math.max(0, Math.ceil((new Date(trialEndDate) - new Date()) / (1000 * 60 * 60 * 24))) : 0;
  const isTrialExpired = trialEndDate && !isTrialActive && user?.subscription_tier === 'free';
  const isImpersonating = user?.impersonating;

  // Load dashboard stats on mount
  React.useEffect(() => {
    axios.get(`${API}/dashboard/stats`)
      .then(r => setDashStats(r.data))
      .catch(() => {});
    // Load activity feed
    axios.get(`${API}/activity/feed`)
      .then(r => setActivityFeed(r.data.activities || []))
      .catch(() => {});
  }, [content]); // Refresh when content changes (after generation)

  const handleSave = async () => {
    if (!saveTitle.trim()) return;
    setIsSaving(true);
    
    try {
      const contentToSave = isEditing ? editedContent : content;
      const response = await axios.post(`${API}/content/save`, {
        title: saveTitle,
        content_type: mode,
        content: contentToSave,
        form_data: getCurrentForm()
      });
      
      setCurrentContentId(response.data.id);
      setShowSaveModal(false);
      setSaveTitle('');
    } catch {
      setError('Failed to save content');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdits = async () => {
    if (!currentContentId) {
      setContent(editedContent);
      setIsEditing(false);
      return;
    }
    
    try {
      await axios.put(`${API}/content/${currentContentId}/update`, 
        { content: editedContent }
      );
      setContent(editedContent);
      setIsEditing(false);
    } catch {
      setError('Failed to save edits');
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateForm.name.trim()) {
      setError('Please enter a template name');
      return;
    }
    
    try {
      await axios.post(`${API}/templates/custom`, {
        ...templateForm,
        content_type: mode,
        default_scriptures: getCurrentForm().scriptures || '',
        default_weeks: getCurrentForm().num_weeks || getCurrentForm().num_sessions || 4,
        focus_areas: getCurrentForm().focus_areas || []
      });
      
      setShowSaveTemplateModal(false);
      setTemplateForm({ name: '', category: 'custom', description: '', default_scriptures: '', default_weeks: 4, focus_areas: [], outline: '' });
    } catch {
      setError('Failed to save template');
    }
  };

  const handleShare = async () => {
    if (!currentContentId) {
      setError('Please save the study first before sharing');
      return;
    }
    
    try {
      const response = await axios.put(
        `${API}/content/${currentContentId}/share?is_public=true`,
        {}
      );
      setShareUrl(`${FRONTEND_URL}/share/${response.data.share_id}`);
      setShowShareModal(true);
    } catch {
      setError('Failed to create share link');
    }
  };

  const copyShareUrl = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadLibrary = async () => {
    setShowLibrary(true);
    try {
      const response = await axios.get(`${API}/content/list`);
      setSavedItems(response.data);
    } catch {
      setError('Failed to load library');
    }
  };

  const loadContent = async (item) => {
    try {
      const response = await axios.get(`${API}/content/${item.id}`);
      setContent(response.data.content);
      setEditedContent(response.data.content);
      setMode(response.data.content_type);
      setCurrentContentId(item.id);
      setShowLibrary(false);
      setIsEditing(false);
    } catch {
      setError('Failed to load content');
    }
  };

  const deleteContent = async (id) => {
    try {
      await axios.delete(`${API}/content/${id}`);
      setSavedItems(items => items.filter(item => item.id !== id));
    } catch {
      setError('Failed to delete content');
    }
  };

  const addMainPoint = () => {
    setSermonNotesForm(prev => ({
      ...prev,
      main_points: [...prev.main_points, '']
    }));
  };

  const updateMainPoint = (index, value) => {
    setSermonNotesForm(prev => ({
      ...prev,
      main_points: prev.main_points.map((p, i) => i === index ? value : p)
    }));
  };

  const removeMainPoint = (index) => {
    if (sermonNotesForm.main_points.length > 1) {
      setSermonNotesForm(prev => ({
        ...prev,
        main_points: prev.main_points.filter((_, i) => i !== index)
      }));
    }
  };

  const renderForm = () => {
    if (mode === 'sermon-notes') {
      return (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Sermon Title *</label>
            <input
              type="text"
              value={sermonNotesForm.sermon_title}
              onChange={(e) => setSermonNotesForm(prev => ({ ...prev, sermon_title: e.target.value }))}
              className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
              placeholder='e.g., "Walking by Faith"'
              data-testid="sermon-title-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Scripture Focus *</label>
            <input
              type="text"
              value={sermonNotesForm.scriptures}
              onChange={(e) => setSermonNotesForm(prev => ({ ...prev, scriptures: e.target.value }))}
              className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
              placeholder="e.g., Hebrews 11:1-6"
              data-testid="sermon-scriptures-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-[#0F1A2A]">Main Points *</label>
              <button
                onClick={addMainPoint}
                className="text-sm text-[#B8860B] hover:text-[#1B3A6B] flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Point
              </button>
            </div>
            <div className="space-y-2">
              {sermonNotesForm.main_points.map((point, index) => (
                <div key={index} className="flex gap-2">
                  <span className="w-6 h-10 flex items-center justify-center text-sm text-[#475569]">{index + 1}.</span>
                  <input
                    type="text"
                    value={point}
                    onChange={(e) => updateMainPoint(index, e.target.value)}
                    className="flex-1 px-4 py-2 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
                    placeholder={`Main point ${index + 1}`}
                  />
                  {sermonNotesForm.main_points.length > 1 && (
                    <button
                      onClick={() => removeMainPoint(index)}
                      className="p-2 text-[#475569] hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Target Audience</label>
            <select
              value={sermonNotesForm.audience}
              onChange={(e) => setSermonNotesForm(prev => ({ ...prev, audience: e.target.value }))}
              className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
            >
              {AUDIENCES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">
              <Globe className="w-4 h-4 inline mr-1" />
              Output Language
            </label>
            <select
              value={sermonNotesForm.language}
              onChange={(e) => setSermonNotesForm(prev => ({ ...prev, language: e.target.value }))}
              className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
            >
              {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
        </div>
      );
    }

    const form = getCurrentForm();

    return (
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">{t('series_title') || 'Series Title'} *</label>
          <input
            type="text"
            value={form.series_title}
            onChange={(e) => setCurrentForm({ series_title: e.target.value })}
            className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
            placeholder='e.g., "Unleashed to Walk in Divine Favor"'
            data-testid={`${mode}-title-input`}
          />
        </div>
        
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-medium text-[#0F1A2A]">{t('scriptures') || 'Key Scriptures'} *</label>
            <button
              onClick={() => setShowScripture(true)}
              className="text-sm text-[#B8860B] hover:text-[#1B3A6B] flex items-center gap-1"
            >
              <Search className="w-4 h-4" />
              Lookup
            </button>
          </div>
          <textarea
            value={form.scriptures}
            onChange={(e) => setCurrentForm({ scriptures: e.target.value })}
            className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none resize-none"
            rows={3}
            placeholder="e.g., Genesis 12:1-3, Psalm 23, Romans 8:28"
            data-testid={`${mode}-scriptures-input`}
          />
        </div>

        {mode === 'bible-study' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">{t('weeks') || 'Number of Weeks'}</label>
                <input
                  type="number" min={1} max={12}
                  value={form.num_weeks}
                  onChange={(e) => setCurrentForm({ num_weeks: parseInt(e.target.value) || 1 })}
                  className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Theme Month</label>
                <input
                  type="text"
                  value={form.theme_month}
                  onChange={(e) => setCurrentForm({ theme_month: e.target.value })}
                  className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
                  placeholder="e.g., January"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Focus Areas</label>
              <div className="flex flex-wrap gap-2">
                {FOCUS_AREAS.map(area => (
                  <button
                    key={area}
                    type="button"
                    onClick={() => {
                      const newAreas = form.focus_areas.includes(area)
                        ? form.focus_areas.filter(a => a !== area)
                        : [...form.focus_areas, area];
                      setCurrentForm({ focus_areas: newAreas });
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      form.focus_areas.includes(area)
                        ? 'bg-[#1B3A6B] text-white'
                        : 'bg-white text-[#475569] hover:bg-[#E2DFD8]'
                    }`}
                  >
                    {area}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {mode === 'devotional' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Duration</label>
              <select
                value={form.duration}
                onChange={(e) => setCurrentForm({ duration: e.target.value })}
                className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
              >
                {DEVOTIONAL_DURATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Entry Length</label>
              <select
                value={form.entry_length}
                onChange={(e) => setCurrentForm({ entry_length: e.target.value })}
                className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
              >
                {ENTRY_LENGTHS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
          </div>
        )}

        {mode === 'curriculum' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Number of Sessions</label>
                <input
                  type="number" min={1} max={12}
                  value={form.num_sessions}
                  onChange={(e) => setCurrentForm({ num_sessions: parseInt(e.target.value) || 1 })}
                  className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Session Length</label>
                <select
                  value={form.session_length}
                  onChange={(e) => setCurrentForm({ session_length: e.target.value })}
                  className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
                >
                  {SESSION_LENGTHS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Teaching Format</label>
              <select
                value={form.teaching_format}
                onChange={(e) => setCurrentForm({ teaching_format: e.target.value })}
                className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
              >
                {TEACHING_FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </>
        )}

        {/* Common fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">{t('audience') || 'Target Audience'}</label>
            <select
              value={form.audience}
              onChange={(e) => setCurrentForm({ audience: e.target.value })}
              className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
            >
              {AUDIENCES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">{t('tone') || 'Tone'}</label>
            <select
              value={form.tone}
              onChange={(e) => setCurrentForm({ tone: e.target.value })}
              className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
            >
              {TONES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">
            <Globe className="w-4 h-4 inline mr-1" />
            Output Language
          </label>
          <select
            value={form.language}
            onChange={(e) => setCurrentForm({ language: e.target.value })}
            className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
          >
            {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Denomination (optional)</label>
          <select
            value={DENOMINATIONS.includes(form.denomination) || form.denomination === '' ? form.denomination : 'Other'}
            onChange={(e) => {
              const val = e.target.value;
              if (val === 'Other') {
                setCurrentForm({ denomination: 'Other' });
                setCustomDenomination('');
              } else {
                setCurrentForm({ denomination: val });
                setCustomDenomination('');
              }
            }}
            className="w-full px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
            data-testid="denomination-dropdown"
          >
            <option value="">Select denomination...</option>
            {DENOMINATIONS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          {(form.denomination === 'Other' || (!DENOMINATIONS.includes(form.denomination) && form.denomination !== '')) && (
            <input
              type="text"
              value={customDenomination || (form.denomination !== 'Other' ? form.denomination : '')}
              onChange={(e) => {
                setCustomDenomination(e.target.value);
                setCurrentForm({ denomination: e.target.value || 'Other' });
              }}
              className="w-full mt-2 px-4 py-3 bg-white rounded-lg shadow-sm focus:ring-2 focus:ring-[#1B3A6B] border-none"
              placeholder="Enter your denomination"
              data-testid="denomination-other-input"
            />
          )}
        </div>
      </div>
    );
  };

  const displayContent = isEditing ? editedContent : content;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="main-app">
      {/* Header */}
      <header className="bg-white border-b border-[#E2DFD8]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {user?.logo_url ? (
              <img src={user.logo_url} alt="Logo" className="w-10 h-10 object-contain" />
            ) : null}
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-[#3D3229] tracking-wide uppercase" style={{fontFamily:"'Nunito', 'Quicksand', sans-serif", letterSpacing:'0.12em'}}>
                {user?.church_name || 'dsashun'}
              </h1>
            </div>
          </div>
          
          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {/* CREATE dropdown */}
            <div className="relative" onMouseLeave={() => setOpenDropdown(null)}>
              <button
                onMouseEnter={() => setOpenDropdown('create')}
                onClick={() => setOpenDropdown(openDropdown === 'create' ? null : 'create')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${openDropdown === 'create' ? 'bg-[#1B3A6B] text-white' : 'text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC]'}`}
                data-testid="nav-create"
              >
                <Sparkles className="w-4 h-4" /> Create <ChevronDown className="w-3 h-3" />
              </button>
              {openDropdown === 'create' && (
                <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-xl shadow-xl border border-[#E2DFD8] py-2 z-50" data-testid="nav-create-dropdown">
                  <button onClick={() => { setShowTemplates(true); setOpenDropdown(null); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#475569] hover:bg-[#F4F2EC] hover:text-[#1B3A6B]" data-testid="templates-button">
                    <LayoutTemplate className="w-4 h-4" /> Templates
                  </button>
                  <button onClick={() => { setShowPlanner(true); setOpenDropdown(null); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#475569] hover:bg-[#F4F2EC] hover:text-[#1B3A6B]" data-testid="planner-button">
                    <Calendar className="w-4 h-4" /> Sermon Planner
                  </button>
                  <button onClick={() => { setShowScheduler(true); setOpenDropdown(null); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#475569] hover:bg-[#F4F2EC] hover:text-[#1B3A6B]" data-testid="scheduler-button">
                    <Calendar className="w-4 h-4" /> Church Schedule
                  </button>
                </div>
              )}
            </div>

            {/* TOOLS dropdown */}
            <div className="relative" onMouseLeave={() => setOpenDropdown(null)}>
              <button
                onMouseEnter={() => setOpenDropdown('tools')}
                onClick={() => setOpenDropdown(openDropdown === 'tools' ? null : 'tools')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${openDropdown === 'tools' ? 'bg-[#1B3A6B] text-white' : 'text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC]'}`}
                data-testid="nav-tools"
              >
                <Layers className="w-4 h-4" /> Tools <ChevronDown className="w-3 h-3" />
              </button>
              {openDropdown === 'tools' && (
                <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-xl shadow-xl border border-[#E2DFD8] py-2 z-50" data-testid="nav-tools-dropdown">
                  <button onClick={() => { setShowCompare(true); setOpenDropdown(null); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#475569] hover:bg-[#F4F2EC] hover:text-[#1B3A6B]" data-testid="compare-button">
                    <Globe className="w-4 h-4" /> Compare Religions
                  </button>
                  <button onClick={() => { setShowScripture(true); setOpenDropdown(null); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#475569] hover:bg-[#F4F2EC] hover:text-[#1B3A6B]" data-testid="scripture-button">
                    <BookOpen className="w-4 h-4" /> Find Scripture
                  </button>
                  <button onClick={() => { setShowSearch(true); setOpenDropdown(null); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#475569] hover:bg-[#F4F2EC] hover:text-[#1B3A6B]" data-testid="search-archive-button">
                    <Search className="w-4 h-4" /> Search Archive
                  </button>
                </div>
              )}
            </div>

            {/* MY STUFF dropdown */}
            <div className="relative" onMouseLeave={() => setOpenDropdown(null)}>
              <button
                onMouseEnter={() => setOpenDropdown('stuff')}
                onClick={() => setOpenDropdown(openDropdown === 'stuff' ? null : 'stuff')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${openDropdown === 'stuff' ? 'bg-[#1B3A6B] text-white' : 'text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC]'}`}
                data-testid="nav-mystuff"
              >
                <FolderOpen className="w-4 h-4" /> My Stuff <ChevronDown className="w-3 h-3" />
              </button>
              {openDropdown === 'stuff' && (
                <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-xl shadow-xl border border-[#E2DFD8] py-2 z-50" data-testid="nav-mystuff-dropdown">
                  <button onClick={() => { loadLibrary(); setOpenDropdown(null); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#475569] hover:bg-[#F4F2EC] hover:text-[#1B3A6B]" data-testid="library-button">
                    <BookMarked className="w-4 h-4" /> Library
                  </button>
                  <button onClick={() => { setShowStorage(true); setOpenDropdown(null); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#475569] hover:bg-[#F4F2EC] hover:text-[#1B3A6B]" data-testid="storage-button">
                    <HardDrive className="w-4 h-4" /> Documents
                  </button>
                </div>
              )}
            </div>

            {/* Direct nav items */}
            <button onClick={() => navigate('/pricing')} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#B8860B] hover:text-[#D19C11] hover:bg-[#FFF8E7] rounded-lg transition-colors" data-testid="pricing-button">
              <CreditCard className="w-4 h-4" /> Pricing
            </button>
            
            <button onClick={() => navigate('/guide')} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg transition-colors" data-testid="guide-button">
              <HelpCircle className="w-4 h-4" /> Guide
            </button>
            
            <button onClick={() => setShowSupport(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg transition-colors" data-testid="support-button">
              <MessageSquare className="w-4 h-4" /> Support
            </button>
            
            {(user?.role === 'admin' || user?.role === 'platform_admin') && (
              <button onClick={() => setShowAdmin(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg transition-colors" data-testid="admin-button">
                <Shield className="w-4 h-4" /> Admin
              </button>
            )}

            <div className="h-6 w-px bg-[#E2DFD8] mx-1"></div>

            <button onClick={() => setShowScriptureLookup(true)} className="p-2 text-[#475569] hover:text-[#B8860B] hover:bg-[#FFF8E7] rounded-lg transition-colors relative group" data-testid="scripture-lookup-trigger" title="Scripture Lookup (Ctrl+K)">
              <BookOpen className="w-4 h-4" />
              <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[9px] text-white bg-gray-800 px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">⌘K</span>
            </button>

            <button onClick={() => setShowSettings(true)} className="p-2 text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg transition-colors" data-testid="settings-button">
              <Settings className="w-4 h-4" />
            </button>
            
            <span className="text-xs text-[#94A3B8] px-1">{user?.name}</span>
            
            <button onClick={logout} className="p-2 text-[#475569] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          {/* Mobile hamburger */}
          <button 
            className="md:hidden p-2 text-[#475569] hover:bg-[#F4F2EC] rounded-lg" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#E2DFD8] bg-white px-4 py-3 space-y-1" data-testid="mobile-menu">
            <p className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider px-4 pt-1">Create</p>
            <button onClick={() => { setShowTemplates(true); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg text-left text-sm">
              <LayoutTemplate className="w-4 h-4" /> Templates
            </button>
            <button onClick={() => { setShowPlanner(true); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg text-left text-sm">
              <Calendar className="w-4 h-4" /> Sermon Planner
            </button>
            <button onClick={() => { setShowScheduler(true); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg text-left text-sm" data-testid="mobile-scheduler-btn">
              <Calendar className="w-4 h-4" /> Church Schedule
            </button>
            
            <p className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider px-4 pt-3">Tools</p>
            <button onClick={() => { setShowCompare(true); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg text-left text-sm" data-testid="mobile-compare-btn">
              <Globe className="w-4 h-4" /> Compare Religions
            </button>
            <button onClick={() => { setShowScripture(true); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg text-left text-sm">
              <BookOpen className="w-4 h-4" /> Find Scripture
            </button>
            <button onClick={() => { setShowSearch(true); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg text-left text-sm">
              <Search className="w-4 h-4" /> Search Archive
            </button>
            
            <p className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider px-4 pt-3">My Stuff</p>
            <button onClick={() => { loadLibrary(); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg text-left text-sm">
              <BookMarked className="w-4 h-4" /> Library
            </button>
            <button onClick={() => { setShowStorage(true); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg text-left text-sm" data-testid="mobile-storage-btn">
              <HardDrive className="w-4 h-4" /> Documents
            </button>
            
            <div className="border-t border-[#E2DFD8] my-2"></div>
            <button onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[#B8860B] hover:text-[#D19C11] hover:bg-[#FFF8E7] rounded-lg text-left text-sm">
              <CreditCard className="w-4 h-4" /> Pricing
            </button>
            <button onClick={() => { navigate('/guide'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg text-left text-sm">
              <HelpCircle className="w-4 h-4" /> Guide & How-To
            </button>
            {(user?.role === 'admin' || user?.role === 'platform_admin') && (
              <button onClick={() => { setShowAdmin(true); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg text-left text-sm">
                <Shield className="w-4 h-4" /> Admin
              </button>
            )}
            <button onClick={() => { setShowSettings(true); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[#475569] hover:text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg text-left text-sm">
              <Settings className="w-4 h-4" /> Settings
            </button>
            <div className="border-t border-[#E2DFD8] pt-2 mt-2">
              <div className="flex items-center justify-between px-4 py-2">
                <span className="text-sm text-[#475569]">{user?.name}</span>
                <button onClick={logout} className="text-sm text-red-500 hover:text-red-700 font-medium" data-testid="mobile-logout-btn">
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-red-600 text-white px-6 py-2" data-testid="impersonation-banner">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <span className="text-sm font-medium">
              Viewing as: <strong>{user?.name}</strong> ({user?.email}) — Impersonation Mode
            </span>
            <button onClick={exitImpersonation} className="text-xs font-bold px-3 py-1.5 bg-white text-red-600 rounded-lg hover:bg-red-50" data-testid="exit-impersonation-btn">
              Exit Impersonation
            </button>
          </div>
        </div>
      )}

      {/* Trial Banner */}
      {isTrialActive && !trialDismissed && (
        <div className="bg-gradient-to-r from-[#1B3A6B] to-[#254B8A] text-white px-6 py-3" data-testid="trial-banner">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-[#B8860B]" />
              <span className="text-sm font-medium">
                Pro Trial Active — {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} remaining. Enjoy unlimited access to all features!
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/pricing')} className="text-xs font-medium px-3 py-1.5 bg-[#B8860B] rounded-lg hover:bg-[#D19C11] transition-colors">Upgrade Now</button>
              <button onClick={() => setTrialDismissed(true)} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}

      {/* Trial Expired Banner */}
      {isTrialExpired && !trialDismissed && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3" data-testid="trial-expired-banner">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">
                Your Pro trial has ended. You're now on the Free plan (5 generations/month). Upgrade to keep all features.
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/pricing')} className="text-xs font-medium px-3 py-1.5 bg-[#1B3A6B] text-white rounded-lg hover:bg-[#254B8A] transition-colors">View Plans</button>
              <button onClick={() => setTrialDismissed(true)} className="text-amber-400 hover:text-amber-600"><X className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto p-4 sm:p-6">
        {mode === 'dashboard' ? (
          /* ===== DASHBOARD CARDS VIEW ===== */
          <div className="min-h-[calc(100vh-120px)]">
            {/* Dashboard Header with Stats */}
            <div className="flex flex-col md:flex-row gap-4 mb-8">
              {/* Welcome + Stats */}
              <div className="flex-1">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#1B3A6B] mb-1" style={{fontFamily:"'Cormorant Garamond', serif"}}>
                  Welcome back, {user?.name?.split(' ')[0] || 'friend'}
                </h2>
                <p className="text-sm text-[#475569]">What would you like to create today?</p>
              </div>
              {dashStats && (
                <div className="flex items-center gap-4 px-5 py-3 bg-white rounded-xl border border-[#E2DFD8] shadow-sm" data-testid="dashboard-stats-bar">
                  <div className="text-center">
                    <p className="text-lg font-bold text-[#1B3A6B]">{dashStats.generations.used}</p>
                    <p className="text-[10px] text-[#94A3B8] uppercase tracking-wide">Generated</p>
                  </div>
                  <div className="h-8 w-px bg-[#E2DFD8]"></div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-[#B8860B]">{dashStats.tier_name}</p>
                    <p className="text-[10px] text-[#94A3B8] uppercase tracking-wide">Plan</p>
                  </div>
                  {!dashStats.generations.unlimited && (
                    <>
                      <div className="h-8 w-px bg-[#E2DFD8]"></div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-[#475569]">{dashStats.generations.limit - dashStats.generations.used}</p>
                        <p className="text-[10px] text-[#94A3B8] uppercase tracking-wide">Remaining</p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Quick Create Hero Card */}
            <div
              id="quick-create-card"
              className={`relative mb-8 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-[#1B3A6B] via-[#254B8A] to-[#1B3A6B] text-white shadow-lg cursor-pointer group overflow-hidden transition-transform hover:scale-[1.01] ${tourStep === 1 ? 'ring-4 ring-[#B8860B] ring-offset-2' : ''}`}
              onClick={handleQuickCreate}
              data-testid="quick-create-card"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#B8860B]/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-[#B8860B]" />
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#B8860B]">Quick Create</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2">Generate a 1-Week Bible Study Instantly</h3>
                <p className="text-sm text-white/70 max-w-lg mb-4">One click, smart defaults, AI-powered — perfect for exploring what dsashun can do.</p>
                <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#B8860B] rounded-lg text-sm font-medium group-hover:bg-[#D19C11] transition-colors">
                  {quickCreating ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4" /> Try It Now <ChevronRight className="w-4 h-4" /></>
                  )}
                </div>
              </div>
            </div>

            {/* AI Generators Section */}
            <div id="generators-section" className={`mb-8 ${tourStep === 2 ? 'ring-4 ring-[#B8860B] ring-offset-4 rounded-2xl' : ''}`}>
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#94A3B8] mb-3 px-1">AI Generators</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {[
                  { key: 'bible-study', label: 'Bible Study', desc: 'Multi-week series with discussion questions', icon: BookOpen, color: 'from-[#1B3A6B] to-[#254B8A]' },
                  { key: 'devotional', label: 'Devotional', desc: 'Daily or weekly devotional entries', icon: Heart, color: 'from-[#7C3AED] to-[#6D28D9]' },
                  { key: 'curriculum', label: 'Curriculum', desc: 'Teaching sessions with activities', icon: GraduationCap, color: 'from-[#059669] to-[#047857]' },
                  { key: 'sermon-notes', label: 'Sermon Notes', desc: 'Fill-in-the-blank congregation notes', icon: Mic, color: 'from-[#B8860B] to-[#9A7209]' },
                ].map(item => (
                  <button
                    key={item.key}
                    onClick={() => setMode(item.key)}
                    className="group relative bg-white rounded-xl p-4 sm:p-5 border border-[#E2DFD8] hover:border-[#1B3A6B]/30 hover:shadow-md transition-all text-left overflow-hidden"
                    data-testid={`card-${item.key}`}
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center mb-3`}>
                      <item.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <h4 className="font-semibold text-sm text-[#0F1A2A] mb-1">{item.label}</h4>
                    <p className="text-xs text-[#94A3B8] leading-relaxed hidden sm:block">{item.desc}</p>
                    <ChevronRight className="absolute top-1/2 right-3 -translate-y-1/2 w-4 h-4 text-[#D1CBC0] opacity-0 group-hover:opacity-100 group-hover:translate-x-0 -translate-x-2 transition-all" />
                  </button>
                ))}
              </div>
            </div>

            {/* Ministry Tools Section */}
            <div id="tools-section" className={`mb-8 ${tourStep === 3 ? 'ring-4 ring-[#B8860B] ring-offset-4 rounded-2xl' : ''}`}>
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#94A3B8] mb-3 px-1">Ministry Tools</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {[
                  { key: 'scheduler', label: 'Church Scheduler', desc: 'Manage services, events & members', icon: Calendar, action: () => setShowScheduler(true) },
                  { key: 'planner', label: 'Sermon Planner', desc: 'Plan your sermon series calendar', icon: Calendar, action: () => setShowPlanner(true) },
                  { key: 'compare', label: 'Compare Religions', desc: 'Cross-religion topic analysis', icon: Globe, action: () => setShowCompare(true) },
                  { key: 'scripture', label: 'Find Scripture', desc: 'Type a phrase, find matching verses', icon: BookOpen, action: () => setShowScripture(true) },
                  { key: 'todo', label: 'To-Do Tasks', desc: 'Personal and shared task management', icon: Check, action: () => setShowTodo(true) },
                  { key: 'flyer', label: 'Flyer Generator', desc: 'AI-generated church flyers & graphics', icon: Image, action: () => setShowFlyer(true) },
                  { key: 'quiz', label: 'Interactive Quiz', desc: 'Live quizzes for your congregation', icon: Layers, action: () => setShowQuiz(true) },
                  { key: 'presentation', label: 'Presentations', desc: 'Prezi-style slides with cool transitions', icon: Monitor, action: () => setShowPresentation(true) },
                  { key: 'preaching-notes', label: 'Sermon Notes', desc: 'Structured preaching notes with doctrine, illustration & application', icon: BookOpen, action: () => setShowSermonNotes(true) },
                  { key: 'propresenter', label: 'Live Presenter', desc: 'ProPresenter-style worship lyrics, scripture & live display', icon: Volume2, action: () => setShowProPresenter(true) },
                ].map(item => (
                  <button
                    key={item.key}
                    onClick={item.action}
                    className="group relative bg-white rounded-xl p-4 sm:p-5 border border-[#E2DFD8] hover:border-[#1B3A6B]/30 hover:shadow-md transition-all text-left overflow-hidden"
                    data-testid={`card-${item.key}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#F4F2EC] flex items-center justify-center mb-3 group-hover:bg-[#1B3A6B]/10 transition-colors">
                      <item.icon className="w-5 h-5 text-[#1B3A6B]" strokeWidth={1.5} />
                    </div>
                    <h4 className="font-semibold text-sm text-[#0F1A2A] mb-1">{item.label}</h4>
                    <p className="text-xs text-[#94A3B8] leading-relaxed hidden sm:block">{item.desc}</p>
                    <ChevronRight className="absolute top-1/2 right-3 -translate-y-1/2 w-4 h-4 text-[#D1CBC0] opacity-0 group-hover:opacity-100 group-hover:translate-x-0 -translate-x-2 transition-all" />
                  </button>
                ))}
              </div>
            </div>

            {/* Your Content Section */}
            <div id="content-section" className={`mb-8 ${tourStep === 4 ? 'ring-4 ring-[#B8860B] ring-offset-4 rounded-2xl' : ''}`}>
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#94A3B8] mb-3 px-1">Your Content</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {[
                  { key: 'library', label: 'My Library', desc: 'Saved studies & generations', icon: BookMarked, action: () => loadLibrary() },
                  { key: 'documents', label: 'Documents', desc: 'Uploaded files & storage', icon: HardDrive, action: () => setShowStorage(true) },
                  { key: 'templates', label: 'Templates', desc: 'Browse pre-built templates', icon: LayoutTemplate, action: () => setShowTemplates(true) },
                  { key: 'team', label: 'Team Members', desc: 'Invite or create team accounts', icon: Users, action: () => setShowTeam(true) },
                ].map(item => (
                  <button
                    key={item.key}
                    onClick={item.action}
                    className="group relative bg-white rounded-xl p-4 sm:p-5 border border-[#E2DFD8] hover:border-[#1B3A6B]/30 hover:shadow-md transition-all text-left overflow-hidden"
                    data-testid={`card-${item.key}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#FFF8E7] flex items-center justify-center mb-3 group-hover:bg-[#B8860B]/15 transition-colors">
                      <item.icon className="w-5 h-5 text-[#B8860B]" strokeWidth={1.5} />
                    </div>
                    <h4 className="font-semibold text-sm text-[#0F1A2A] mb-1">{item.label}</h4>
                    <p className="text-xs text-[#94A3B8] leading-relaxed hidden sm:block">{item.desc}</p>
                    <ChevronRight className="absolute top-1/2 right-3 -translate-y-1/2 w-4 h-4 text-[#D1CBC0] opacity-0 group-hover:opacity-100 group-hover:translate-x-0 -translate-x-2 transition-all" />
                  </button>
                ))}
              </div>
            </div>

            {/* Pastor's Toolkit */}
            <div className="mb-8">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#94A3B8] mb-3 px-1">Pastor's Toolkit</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                {[
                  { key: 'illustrations', label: 'Sermon Illustrations', desc: 'Find compelling stories for your sermons', icon: Lightbulb, action: () => setShowIllustrations(true) },
                  { key: 'handout', label: 'Lesson Handouts', desc: 'Printable fill-in-the-blank worksheets', icon: ClipboardList, action: () => setShowHandout(true) },
                  { key: 'counseling', label: 'Counseling Notes', desc: 'Private pastoral session templates', icon: Shield, action: () => setShowCounseling(true) },
                ].map(item => (
                  <button
                    key={item.key}
                    onClick={item.action}
                    className="group relative bg-white rounded-xl p-4 sm:p-5 border border-[#E2DFD8] hover:border-[#1B3A6B]/30 hover:shadow-md transition-all text-left overflow-hidden"
                    data-testid={`card-${item.key}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#1B3A6B]/10 to-[#B8860B]/10 flex items-center justify-center mb-3 group-hover:from-[#1B3A6B]/20 group-hover:to-[#B8860B]/20 transition-colors">
                      <item.icon className="w-5 h-5 text-[#1B3A6B]" strokeWidth={1.5} />
                    </div>
                    <h4 className="font-semibold text-sm text-[#0F1A2A] mb-1">{item.label}</h4>
                    <p className="text-xs text-[#94A3B8] leading-relaxed hidden sm:block">{item.desc}</p>
                    <ChevronRight className="absolute top-1/2 right-3 -translate-y-1/2 w-4 h-4 text-[#D1CBC0] opacity-0 group-hover:opacity-100 group-hover:translate-x-0 -translate-x-2 transition-all" />
                  </button>
                ))}
              </div>
            </div>

            {/* Team Activity Feed */}
            {activityFeed.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xs font-bold uppercase tracking-widest text-[#94A3B8] mb-3 px-1 flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5" /> Team Activity
                </h3>
                <div className="bg-white rounded-xl border border-[#E2DFD8] divide-y divide-[#F0EDE6]" data-testid="activity-feed">
                  {activityFeed.slice(0, 8).map(act => (
                    <div key={act.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                      <div className="w-7 h-7 rounded-full bg-[#1B3A6B] flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                        {act.user_name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-[#0F1A2A]">{act.user_name}</span>{' '}
                        <span className="text-[#475569]">
                          {act.action === 'task_completed' && 'completed a task:'}
                          {act.action === 'member_added' && 'added a team member:'}
                          {act.action === 'content_generated' && 'generated content:'}
                        </span>{' '}
                        <span className="text-[#0F1A2A] font-medium">{act.detail}</span>
                      </div>
                      <span className="text-[10px] text-[#94A3B8] flex-shrink-0">
                        {new Date(act.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Activity */}
            {dashStats?.recent_content?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xs font-bold uppercase tracking-widest text-[#94A3B8] mb-3 px-1">Recent Activity</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {dashStats.recent_content.slice(0, 3).map(item => (
                    <button
                      key={item.id}
                      onClick={() => { loadSearchResult(item); }}
                      className="bg-white rounded-xl p-4 border border-[#E2DFD8] hover:border-[#1B3A6B]/30 hover:shadow-sm transition-all text-left"
                      data-testid={`recent-card-${item.id}`}
                    >
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-[#B8860B]">{item.content_type?.replace('-', ' ')}</span>
                      <p className="text-sm font-medium text-[#0F1A2A] mt-1 truncate">{item.title || 'Untitled'}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Guided Tour Overlay */}
            {tourStep >= 0 && (
              <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="guided-tour-overlay">
                <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-sm w-full text-center" data-testid="tour-dialog">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1B3A6B] to-[#254B8A] flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-6 h-6 text-[#B8860B]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1B3A6B] mb-2">{TOUR_STEPS[tourStep]?.title}</h3>
                  <p className="text-sm text-[#475569] mb-6 leading-relaxed">{TOUR_STEPS[tourStep]?.text}</p>
                  <div className="flex items-center justify-between">
                    <button onClick={skipTour} className="text-xs text-[#94A3B8] hover:text-[#475569]" data-testid="tour-skip-btn">
                      Skip tour
                    </button>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        {TOUR_STEPS.map((_, i) => (
                          <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === tourStep ? 'bg-[#1B3A6B]' : 'bg-[#E2DFD8]'}`} />
                        ))}
                      </div>
                      <button
                        onClick={advanceTour}
                        className="flex items-center gap-1.5 px-5 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A] transition-colors"
                        data-testid="tour-next-btn"
                      >
                        {tourStep >= TOUR_STEPS.length - 1 ? 'Get Started' : 'Next'}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
        /* ===== GENERATOR FORM + PREVIEW VIEW ===== */
        <div className="flex flex-col lg:flex-row gap-6 min-h-[calc(100vh-120px)]">
          {/* Left Panel - Form */}
          <div className="w-full lg:w-[40%] flex flex-col">
            {/* Back to Dashboard */}
            <button
              onClick={() => setMode('dashboard')}
              className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#1B3A6B] mb-4 w-fit transition-colors"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>

            {/* Dashboard Stats Widget */}
            {dashStats && (
              <div className="bg-white rounded-xl p-4 mb-4 shadow-sm border border-[#E2DFD8]" data-testid="dashboard-widget">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-[#0F1A2A]">
                    {dashStats.tier_name} Plan
                    {dashStats.trial_active && <span className="ml-2 text-xs px-2 py-0.5 bg-[#B8860B]/10 text-[#B8860B] rounded-full">Trial</span>}
                  </span>
                  <button onClick={() => navigate('/pricing')} className="text-xs text-[#1B3A6B] font-medium hover:underline" data-testid="widget-upgrade-btn">
                    {dashStats.tier === 'church' ? 'Manage' : 'Upgrade'}
                  </button>
                </div>
                
                {/* Generation usage bar */}
                <div className="mb-3">
                  <div className="flex items-center justify-between text-xs text-[#475569] mb-1">
                    <span>Generations this month</span>
                    <span className="font-medium text-[#0F1A2A]">
                      {dashStats.generations.unlimited ? `${dashStats.generations.used} used` : `${dashStats.generations.used} / ${dashStats.generations.limit}`}
                    </span>
                  </div>
                  {!dashStats.generations.unlimited && (
                    <div className="w-full h-2 bg-[#F4F2EC] rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${dashStats.generations.used >= dashStats.generations.limit ? 'bg-red-500' : dashStats.generations.used >= dashStats.generations.limit * 0.8 ? 'bg-amber-500' : 'bg-[#1B3A6B]'}`}
                        style={{ width: `${Math.min(100, (dashStats.generations.used / dashStats.generations.limit) * 100)}%` }}
                        data-testid="usage-bar"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mode Tabs */}
            <div className="bg-[#F4F2EC] rounded-xl p-1.5 mb-6 flex flex-wrap">
              <button onClick={() => setMode('bible-study')} className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-lg font-medium text-sm transition-all ${mode === 'bible-study' ? 'tab-active' : 'text-[#475569] hover:text-[#1B3A6B]'}`} data-testid="mode-tab-bible-study">
                <BookOpen className="w-4 h-4" strokeWidth={1.5} />
                {t('bible_study') || 'Bible Study'}
              </button>
              <button onClick={() => setMode('devotional')} className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-lg font-medium text-sm transition-all ${mode === 'devotional' ? 'tab-active' : 'text-[#475569] hover:text-[#1B3A6B]'}`} data-testid="mode-tab-devotional">
                <Heart className="w-4 h-4" strokeWidth={1.5} />
                {t('devotional') || 'Devotional'}
              </button>
              <button onClick={() => setMode('curriculum')} className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-lg font-medium text-sm transition-all ${mode === 'curriculum' ? 'tab-active' : 'text-[#475569] hover:text-[#1B3A6B]'}`} data-testid="mode-tab-curriculum">
                <GraduationCap className="w-4 h-4" strokeWidth={1.5} />
                {t('curriculum') || 'Curriculum'}
              </button>
              <button onClick={() => setMode('sermon-notes')} className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-lg font-medium text-sm transition-all ${mode === 'sermon-notes' ? 'tab-active' : 'text-[#475569] hover:text-[#1B3A6B]'}`} data-testid="mode-tab-sermon-notes">
                <Mic className="w-4 h-4" strokeWidth={1.5} />
                Sermon Notes
              </button>
            </div>

            {/* Form */}
            <div className="flex-1 overflow-y-auto pr-2">
              {renderForm()}
            </div>

            {/* Error Banner */}
            {error && (
              <div className="error-banner rounded-lg p-3 mt-4 flex items-center gap-2" data-testid="error-banner">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
                <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
              </div>
            )}

            {/* Generate Button */}
            <div className="mt-6 flex gap-3">
              <button onClick={handleGenerate} disabled={isGenerating} className="flex-1 btn-primary py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid="generate-button">
                {isGenerating ? (<><Loader2 className="w-5 h-5 animate-spin" />{generatingProgress || t('generating') || 'Generating...'}</>) : (<><Sparkles className="w-5 h-5" strokeWidth={1.5} />{t('generate') || 'Generate'}</>)}
              </button>
              
              {content && (
                <button onClick={handleGenerate} disabled={isGenerating} className="btn-secondary py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid="regenerate-button">
                  <RefreshCw className="w-5 h-5" strokeWidth={1.5} />
                </button>
              )}
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className={`w-full lg:w-[60%] bg-white rounded-2xl shadow-[0_8px_32px_rgba(27,58,107,0.06)] flex flex-col ${isGenerating ? 'generating' : ''}`}>
            {displayContent ? (
              <>
                {/* Editing Toggle */}
                <div className="flex items-center justify-end p-4 border-b border-[#E2DFD8]">
                  <button
                    onClick={() => {
                      if (isEditing) {
                        handleSaveEdits();
                      } else {
                        setEditedContent(content);
                        setIsEditing(true);
                      }
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isEditing ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC] text-[#475569] hover:bg-[#E2DFD8]'
                    }`}
                    data-testid="edit-toggle"
                  >
                    <Edit3 className="w-4 h-4" />
                    {isEditing ? 'Save Edits' : 'Edit'}
                  </button>
                </div>

                {/* Preview Content */}
                <div className="flex-1 p-12 overflow-y-auto">
                  {(user?.logo_url || user?.church_name) && (
                    <div className="text-center mb-8 pb-6 border-b border-[#E2DFD8]">
                      {user?.logo_url && <img src={user.logo_url} alt="Logo" className="h-12 mx-auto mb-3" />}
                      {user?.church_name && (
                        <p className="text-[#B8860B] font-medium uppercase tracking-wider text-xs">{user.church_name}</p>
                      )}
                    </div>
                  )}
                  
                  {isEditing ? (
                    <textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      className="w-full h-full min-h-[500px] p-4 font-mono text-sm bg-[#F9F8F6] rounded-lg border border-[#E2DFD8] resize-none focus:ring-2 focus:ring-[#1B3A6B]"
                      data-testid="content-editor"
                    />
                  ) : (
                    <div className="preview-content" ref={previewRef}>
                      {/* Visual Banner — AI Cover or Stock */}
                      <div className="relative rounded-xl overflow-hidden mb-8 h-48" style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #254B8A 100%)' }}>
                        <img
                          src={coverImage || "https://images.unsplash.com/photo-1647986886429-0f2166422eb6?w=900&q=80"}
                          alt=""
                          className={`absolute inset-0 w-full h-full object-cover ${coverImage ? 'opacity-60' : 'opacity-25'}`}
                          crossOrigin="anonymous"
                        />
                        {coverImageLoading && (
                          <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/40 px-2.5 py-1 rounded-full">
                            <Loader2 className="w-3 h-3 animate-spin text-white" />
                            <span className="text-white text-[10px] font-medium">Generating cover...</span>
                          </div>
                        )}
                        {coverImage && !coverImageLoading && (
                          <div className="absolute top-3 right-3 flex items-center gap-1 bg-[#B8860B]/80 px-2.5 py-1 rounded-full">
                            <Sparkles className="w-3 h-3 text-white" />
                            <span className="text-white text-[10px] font-medium">AI Generated</span>
                          </div>
                        )}
                        <div className="absolute inset-0 flex flex-col justify-end p-6">
                          <div className="w-12 h-0.5 bg-[#B8860B] mb-3"></div>
                          <h1 className="text-white text-2xl font-bold font-serif leading-tight" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
                            {getCurrentForm().series_title || getCurrentForm().sermon_title || 'Bible Study'}
                          </h1>
                          {user?.church_name && (
                            <p className="text-[#B8860B] text-sm font-medium mt-1 tracking-wide">{user.church_name}</p>
                          )}
                        </div>
                      </div>
                      <ReactMarkdown
                        components={{
                          h1: ({children}) => (
                            <div className="relative mt-10 mb-6 py-4 px-6 rounded-lg" style={{ background: 'linear-gradient(135deg, #1B3A6B, #254B8A)' }}>
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#B8860B] rounded-l-lg"></div>
                              <h1 className="text-white text-xl font-bold font-serif">{children}</h1>
                            </div>
                          ),
                          h2: ({children}) => (
                            <div className="mt-8 mb-4">
                              <h2 className="text-[#1B3A6B] text-lg font-bold font-serif">{children}</h2>
                              <div className="w-16 h-0.5 bg-[#B8860B] mt-2"></div>
                            </div>
                          ),
                          h3: ({children}) => (
                            <h3 className="text-[#B8860B] text-sm font-bold uppercase tracking-wider mt-5 mb-2">{children}</h3>
                          ),
                          blockquote: ({children}) => (
                            <div className="my-4 pl-5 py-3 pr-4 border-l-4 border-[#B8860B] bg-[#FFF8E7] rounded-r-lg italic text-[#1B3A6B]">
                              {children}
                            </div>
                          ),
                          hr: () => (
                            <div className="my-8 flex items-center gap-3">
                              <div className="flex-1 h-px bg-[#E2DFD8]"></div>
                              <div className="w-2 h-2 rounded-full bg-[#B8860B]"></div>
                              <div className="flex-1 h-px bg-[#E2DFD8]"></div>
                            </div>
                          ),
                          li: ({children}) => (
                            <li className="flex items-start gap-2 mb-1.5">
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#B8860B] flex-shrink-0"></span>
                              <span>{children}</span>
                            </li>
                          ),
                          ul: ({children}) => <ul className="list-none pl-2 space-y-0.5">{children}</ul>,
                          strong: ({children}) => <strong className="text-[#0F1A2A] font-bold">{children}</strong>,
                        }}
                      >
                        {displayContent}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>

                {/* Export Toolbar */}
                <div className="sticky bottom-0 backdrop-blur-xl bg-white/70 border-t border-[#1B3A6B]/10 p-4 flex items-center gap-3 rounded-b-2xl flex-wrap">
                  <button onClick={() => { setSaveTitle(getCurrentForm().series_title || getCurrentForm().sermon_title || ''); setShowSaveModal(true); }} className="btn-secondary py-2.5 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="save-button">
                    <Save className="w-4 h-4" strokeWidth={1.5} />
                    {t('save') || 'Save'}
                  </button>
                  
                  <button onClick={() => setShowSaveTemplateModal(true)} className="btn-secondary py-2.5 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="save-template-button">
                    <LayoutTemplate className="w-4 h-4" strokeWidth={1.5} />
                    Save as Template
                  </button>
                  
                  <button onClick={handleShare} className="btn-secondary py-2.5 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="share-button">
                    <Share2 className="w-4 h-4" strokeWidth={1.5} />
                    Share
                  </button>
                  
                  {currentContentId && (
                    <button onClick={() => setShowEmailShare(true)} className="btn-secondary py-2.5 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="email-share-button">
                      <Mail className="w-4 h-4" strokeWidth={1.5} />
                      Email
                    </button>
                  )}
                  
                  <button onClick={handleExportPDF} disabled={isExportingPDF} className="btn-accent py-2.5 px-4 rounded-lg font-medium flex items-center gap-2 text-sm disabled:opacity-50" data-testid="export-pdf-button">
                    {isExportingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" strokeWidth={1.5} />}
                    PDF
                  </button>
                  
                  <button onClick={handleExportWord} className="btn-accent py-2.5 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="export-word-button">
                    <FileText className="w-4 h-4" strokeWidth={1.5} />
                    Word
                  </button>
                  
                  <button onClick={handleExportPPTX} disabled={isExportingPPTX} className="btn-accent py-2.5 px-4 rounded-lg font-medium flex items-center gap-2 text-sm disabled:opacity-50" data-testid="export-pptx-button">
                    {isExportingPPTX ? <Loader2 className="w-4 h-4 animate-spin" /> : <Presentation className="w-4 h-4" strokeWidth={1.5} />}
                    PPTX
                  </button>
                  
                  <button onClick={handleCopy} className="btn-secondary py-2.5 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="copy-button">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" strokeWidth={1.5} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  
                  <button onClick={handlePrint} className="btn-secondary py-2.5 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="print-button">
                    <Printer className="w-4 h-4" strokeWidth={1.5} />
                    Print
                  </button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
                <img src="https://images.unsplash.com/photo-1766364984475-53b7cf4d33fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxvcGVuJTIwYmlibGUlMjBvbiUyMHdvb2RlbiUyMGRlc2slMjBzdW5saWdodHxlbnwwfHx8fDE3NzU2OTUzNDV8MA&ixlib=rb-4.1.0&q=85" alt="Open Bible" className="w-64 h-48 object-cover rounded-xl opacity-40 mb-6" />
                <h3 className="font-heading text-2xl font-semibold text-[#1B3A6B] mb-2">{t('ready_to_create') || 'Ready to Create'}</h3>
                <p className="text-[#475569] max-w-md mb-6">Select a mode, fill in your study details, and click Generate to create your content.</p>
                <button onClick={() => setShowTemplates(true)} className="btn-secondary px-6 py-3 rounded-lg font-medium flex items-center gap-2">
                  <LayoutTemplate className="w-5 h-5" />
                  Browse Templates
                </button>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Modals */}
      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}
      {showScripture && <ScriptureSearch onClose={() => setShowScripture(false)} />}
      {showTemplates && <TemplateLibrary onSelectTemplate={handleTemplateSelect} onClose={() => setShowTemplates(false)} />}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {showPlanner && (
        <SermonPlanner 
          onClose={() => setShowPlanner(false)} 
          onGenerateStudy={(generatedContent, weekData) => {
            setContent(generatedContent);
            setEditedContent(generatedContent);
            setMode('bible-study');
            setBibleStudyForm(prev => ({
              ...prev,
              series_title: weekData?.sermon_title || '',
              scriptures: weekData?.scriptures || ''
            }));
          }}
        />
      )}
      {showCompare && (
        <ReligionCompare
          onClose={() => setShowCompare(false)}
          effectiveTier={user?.effective_tier || user?.subscription_tier || 'free'}
          userRole={user?.role}
        />
      )}
      {showStorage && (
        <DocumentStorage onClose={() => setShowStorage(false)} />
      )}
      {showScheduler && (
        <Scheduler
          onClose={() => setShowScheduler(false)}
          effectiveTier={user?.effective_tier || user?.subscription_tier || 'free'}
        />
      )}
      {showTodo && <TodoTasks onClose={() => setShowTodo(false)} />}
      {showTeam && <TeamManagement onClose={() => setShowTeam(false)} />}
      {showIllustrations && <SermonIllustrations onClose={() => setShowIllustrations(false)} />}
      {showHandout && <LessonHandout onClose={() => setShowHandout(false)} />}
      {showCounseling && <CounselingNotes onClose={() => setShowCounseling(false)} />}
      {showFlyer && <FlyerGenerator onClose={() => setShowFlyer(false)} user={user} />}
      {showSupport && <SupportCenter onClose={() => setShowSupport(false)} />}
      <ScriptureLookupBar isOpen={showScriptureLookup} onClose={() => setShowScriptureLookup(false)} />
      {quizPresentId && <QuizPresenterLive quizId={quizPresentId} onClose={() => setQuizPresentId(null)} />}
      {presPresentId && <PresentMode presId={presPresentId} onClose={() => setPresPresentId(null)} />}
      {showQuiz && !quizPresentId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => { setShowQuiz(false); setQuizEditId(null); }}>
          <div className="bg-[#F9F8F6] w-full sm:rounded-2xl shadow-2xl sm:max-w-5xl h-[95vh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6 rounded-t-2xl" onClick={e => e.stopPropagation()} data-testid="quiz-modal">
            <div className="flex items-center justify-between mb-4">
              <div />
              <button onClick={() => { setShowQuiz(false); setQuizEditId(null); }} className="p-2 hover:bg-[#E2DFD8] rounded-lg transition-colors">
                <X className="w-5 h-5 text-[#475569]" />
              </button>
            </div>
            {quizEditId ? (
              <QuizEditor quizId={quizEditId} onBack={() => setQuizEditId(null)} />
            ) : (
              <QuizManager 
                onPresent={(id) => { setShowQuiz(false); setQuizPresentId(id); }}
                onEdit={(id) => setQuizEditId(id)}
              />
            )}
          </div>
        </div>
      )}
      {showPresentation && !presPresentId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => { setShowPresentation(false); setPresEditId(null); }}>
          <div className="bg-[#F9F8F6] rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="presentation-modal">
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#E2DFD8]">
              <div />
              <button onClick={() => { setShowPresentation(false); setPresEditId(null); }} className="p-2 hover:bg-[#E2DFD8] rounded-lg">
                <X className="w-5 h-5 text-[#475569]" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              {presEditId ? (
                <SlideEditor presId={presEditId} onBack={() => setPresEditId(null)} onPresent={(id) => { setShowPresentation(false); setPresPresentId(id); }} />
              ) : (
                <div className="p-6 overflow-y-auto h-full">
                  <PresentationManager
                    onEdit={(id) => setPresEditId(id)}
                    onPresent={(id) => { setShowPresentation(false); setPresPresentId(id); }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showSermonNotes && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setShowSermonNotes(false)}>
          <div className="bg-[#F9F8F6] w-full sm:rounded-2xl shadow-2xl sm:max-w-4xl h-[95vh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6 rounded-t-2xl" onClick={e => e.stopPropagation()} data-testid="sermon-notes-modal">
            <div className="flex items-center justify-between mb-2">
              <div />
              <button onClick={() => setShowSermonNotes(false)} className="p-2 hover:bg-[#E2DFD8] rounded-lg"><X className="w-5 h-5 text-[#475569]" /></button>
            </div>
            <SermonNotes onClose={() => setShowSermonNotes(false)} />
          </div>
        </div>
      )}
      {showProPresenter && (
        <div className="fixed inset-0 bg-black z-50" data-testid="propresenter-modal">
          <div className="absolute top-2 right-2 z-10">
            <button onClick={() => setShowProPresenter(false)} className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white/60 hover:text-white" data-testid="close-propresenter">
              <X className="w-5 h-5" />
            </button>
          </div>
          <ProPresenter onClose={() => setShowProPresenter(false)} />
        </div>
      )}
      {showEmailShare && currentContentId && (
        <EmailShareModal 
          contentId={currentContentId}
          contentTitle={saveTitle || getCurrentForm().series_title || getCurrentForm().sermon_title || 'Bible Study'}
          onClose={() => setShowEmailShare(false)}
        />
      )}

      {/* Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="save-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="font-heading text-xl font-semibold text-[#1B3A6B] mb-4">Save to Library</h3>
            <input type="text" value={saveTitle} onChange={(e) => setSaveTitle(e.target.value)} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg focus:ring-2 focus:ring-[#1B3A6B] border-none mb-4" placeholder="Enter a title" data-testid="save-title-input" />
            <div className="flex gap-3">
              <button onClick={() => setShowSaveModal(false)} className="flex-1 btn-secondary py-2.5 rounded-lg font-medium">Cancel</button>
              <button onClick={handleSave} disabled={isSaving || !saveTitle.trim()} className="flex-1 btn-primary py-2.5 rounded-lg font-medium disabled:opacity-50" data-testid="confirm-save-button">{isSaving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Save Template Modal */}
      {showSaveTemplateModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="save-template-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="font-heading text-xl font-semibold text-[#1B3A6B] mb-4">Save as Custom Template</h3>
            <div className="space-y-4">
              <input type="text" value={templateForm.name} onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg focus:ring-2 focus:ring-[#1B3A6B] border-none" placeholder="Template name" />
              <select value={templateForm.category} onChange={(e) => setTemplateForm(prev => ({ ...prev, category: e.target.value }))} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg focus:ring-2 focus:ring-[#1B3A6B] border-none">
                <option value="custom">Custom</option>
                <option value="sermon-notes">Sermon Notes</option>
                <option value="small-group">Small Group</option>
                <option value="youth">Youth</option>
                <option value="family">Family</option>
              </select>
              <textarea value={templateForm.description} onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg focus:ring-2 focus:ring-[#1B3A6B] border-none resize-none" rows={2} placeholder="Description" />
              <textarea value={templateForm.outline} onChange={(e) => setTemplateForm(prev => ({ ...prev, outline: e.target.value }))} className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg focus:ring-2 focus:ring-[#1B3A6B] border-none resize-none" rows={3} placeholder="Outline (one item per line)" />
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowSaveTemplateModal(false)} className="flex-1 btn-secondary py-2.5 rounded-lg font-medium">Cancel</button>
              <button onClick={handleSaveAsTemplate} className="flex-1 btn-primary py-2.5 rounded-lg font-medium">Save Template</button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="share-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold text-[#1B3A6B]">Share Study</h3>
              <button onClick={() => setShowShareModal(false)}><X className="w-5 h-5 text-[#475569]" /></button>
            </div>
            <p className="text-[#475569] text-sm mb-4">Anyone with this link can view your study:</p>
            <div className="flex gap-2 mb-4">
              <input type="text" value={shareUrl} readOnly className="flex-1 px-4 py-3 bg-[#F9F8F6] rounded-lg border-none text-sm" />
              <button onClick={copyShareUrl} className="btn-primary px-4 rounded-lg">{copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}</button>
            </div>
            <button onClick={() => setShowShareModal(false)} className="w-full btn-secondary py-2.5 rounded-lg font-medium">Done</button>
          </div>
        </div>
      )}

      {/* Library Modal */}
      {showLibrary && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="library-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl shadow-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold text-[#1B3A6B]">{t('library') || 'Your Library'}</h3>
              <button onClick={() => setShowLibrary(false)}><X className="w-5 h-5 text-[#475569]" /></button>
            </div>
            
            {savedItems.length === 0 ? (
              <div className="text-center py-12 text-[#475569]">
                <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No saved items yet</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-2">
                {savedItems.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-4 bg-[#F9F8F6] rounded-lg hover:bg-[#F4F2EC] transition-colors">
                    <button onClick={() => loadContent(item)} className="flex-1 text-left">
                      <h4 className="font-medium text-[#0F1A2A]">{item.title}</h4>
                      <p className="text-sm text-[#475569]">
                        {item.content_type === 'bible-study' ? 'Bible Study' : item.content_type === 'devotional' ? 'Devotional' : item.content_type === 'sermon-notes' ? 'Sermon Notes' : 'Curriculum'}
                        {' · '}{new Date(item.created_at).toLocaleDateString()}
                        {item.is_public && <span className="ml-2 text-[#B8860B]">· Shared</span>}
                      </p>
                    </button>
                    <button onClick={() => deleteContent(item.id)} className="p-2 text-[#475569] hover:text-red-500 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search Archive Modal */}
      {showSearch && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="search-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl shadow-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold text-[#1B3A6B]">Search Archive</h3>
              <button onClick={() => setShowSearch(false)}><X className="w-5 h-5 text-[#475569]" /></button>
            </div>
            
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]"
                placeholder="Search by title, scripture, or content..."
                data-testid="search-input"
              />
              <button onClick={handleSearch} disabled={searchLoading} className="btn-primary px-5 py-3 rounded-lg font-medium flex items-center gap-2" data-testid="search-submit">
                {searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto space-y-2">
              {searchResults.length === 0 && !searchLoading && searchQuery && (
                <div className="text-center py-8 text-[#475569]">No results found</div>
              )}
              {searchResults.map(item => (
                <button key={item.id} onClick={() => loadSearchResult(item)} className="w-full text-left p-4 bg-[#F9F8F6] rounded-lg hover:bg-[#F4F2EC] transition-colors" data-testid={`search-result-${item.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#1B3A6B]/10 text-[#1B3A6B] capitalize">{item.content_type?.replace('-', ' ')}</span>
                    <span className="text-xs text-[#475569]">{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</span>
                  </div>
                  <h4 className="font-medium text-[#0F1A2A]">{item.title}</h4>
                  {item.preview && <p className="text-sm text-[#475569] mt-1 line-clamp-2">{item.preview}</p>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
